import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileText, CheckCircle, Clock, XCircle, AlertCircle, X } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useGetMarketplaceKycStatusQuery, useSubmitMarketplaceKycMutation } from '../../store/slices/apiSlice';
import toast from 'react-hot-toast';
import Navbar from '../../components/common/Navbar';

const MarketplaceKYC: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const { data: kycStatusResponse, refetch } = useGetMarketplaceKycStatusQuery(undefined, {
    skip: !user,
  });
  const [submitKyc] = useSubmitMarketplaceKycMutation();

  // Clear any existing toasts when KYC page loads
  useEffect(() => {
    toast.dismiss();
  }, []);

  const [kycDocuments, setKycDocuments] = useState<{
    document_front?: File | null;
    document_back?: File | null;
    address_proof?: File | null;
    selfie_image?: File | null;
  }>({});
  const [previews, setPreviews] = useState<{
    document_front?: string;
    document_back?: string;
    address_proof?: string;
    selfie_image?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const kycData = kycStatusResponse?.data;
  const kycStatus = kycData?.status || 'pending';
  const rejectionReason = kycData?.rejection_reason;

  const handleFileChange = (field: 'document_front' | 'document_back' | 'address_proof' | 'selfie_image', file: File | null) => {
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setKycDocuments((prev) => ({ ...prev, [field]: file }));
      setPreviews((prev) => ({ ...prev, [field]: URL.createObjectURL(file) }));
    }
  };

  const handleRemoveFile = (field: 'document_front' | 'document_back' | 'address_proof' | 'selfie_image') => {
    setKycDocuments((prev) => ({ ...prev, [field]: null }));
    setPreviews((prev) => {
      if (prev[field]) {
        URL.revokeObjectURL(prev[field]!);
      }
      const newPreviews = { ...prev };
      delete newPreviews[field];
      return newPreviews;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!kycDocuments.document_front || !kycDocuments.address_proof) {
      toast.error('Please upload at least document front and address proof');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await submitKyc({
        document_type: 'passport', // Default, can be made configurable
        document_front: kycDocuments.document_front!,
        document_back: kycDocuments.document_back || undefined,
        address_proof: kycDocuments.address_proof!,
        selfie_image: kycDocuments.selfie_image || undefined,
      }).unwrap();

      if (result?.success) {
        toast.success('KYC documents submitted successfully! Admin will review your submission.');
        refetch();
        setKycDocuments({});
        setPreviews({});
      } else {
        toast.error(result?.message || 'Failed to submit KYC documents');
      }
    } catch (error: any) {
      toast.error(error?.data?.message || error?.message || 'Failed to submit KYC documents. Please try again.');
      console.error('KYC submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = () => {
    switch (kycStatus) {
      case 'approved':
        return (
          <Badge variant="success" className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="danger" className="flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            Rejected
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="warning" className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Pending Review
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">KYC Verification</h1>
              <p className="text-gray-600">
                Complete KYC verification to increase your transaction limit
              </p>
            </div>
            {getStatusBadge()}
          </div>

          {/* Status Messages */}
          {kycStatus === 'approved' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-green-900 mb-1">KYC Approved</h3>
                  <p className="text-sm text-green-700">
                    Your KYC verification has been approved. You can now make transactions up to your increased limit.
                  </p>
                </div>
              </div>
            </div>
          )}

          {kycStatus === 'rejected' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-red-900 mb-1">KYC Rejected</h3>
                  {rejectionReason && (
                    <p className="text-sm text-red-700 mb-2">
                      <strong>Reason:</strong> {rejectionReason}
                    </p>
                  )}
                  <p className="text-sm text-red-700">
                    Please review the rejection reason and submit new documents.
                  </p>
                </div>
              </div>
            </div>
          )}

          {kycStatus === 'pending' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-yellow-900 mb-1">Under Review</h3>
                  <p className="text-sm text-yellow-700">
                    Your KYC documents are being reviewed by our admin team. You will be notified once the review is complete.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* KYC Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-gray-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Required Documents</h3>
                  <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
                    <li>Government-issued ID (Front and Back)</li>
                    <li>Proof of Address (Utility bill, Bank statement, etc.)</li>
                  </ul>
                  <p className="text-xs text-gray-600 mt-2">
                    All documents must be clear, readable, and less than 5MB each.
                  </p>
                </div>
              </div>
            </div>

            {/* ID Front */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID Front (Government-issued) *
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-white">
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileChange('document_front', e.target.files?.[0] || null)}
                  className="hidden"
                  id="document-front"
                  disabled={kycStatus === 'pending'}
                />
                {!previews.document_front ? (
                  <label
                    htmlFor="document-front"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600 mb-1">Click to upload ID front</span>
                    <span className="text-xs text-gray-500">PNG, JPG, PDF up to 5MB</span>
                  </label>
                ) : (
                  <div className="relative bg-white">
                    <img
                      src={previews.document_front}
                      alt="ID Front"
                      className="w-full h-48 object-contain border border-gray-300 rounded-lg bg-white"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveFile('document_front')}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      disabled={kycStatus === 'pending'}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* ID Back */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID Back (Government-issued) *
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-white">
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileChange('document_back', e.target.files?.[0] || null)}
                  className="hidden"
                  id="document-back"
                  disabled={kycStatus === 'pending'}
                />
                {!previews.document_back ? (
                  <label
                    htmlFor="document-back"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600 mb-1">Click to upload ID back</span>
                    <span className="text-xs text-gray-500">PNG, JPG, PDF up to 5MB</span>
                  </label>
                ) : (
                  <div className="relative bg-white">
                    <img
                      src={previews.document_back}
                      alt="ID Back"
                      className="w-full h-48 object-contain border border-gray-300 rounded-lg bg-white"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveFile('document_back')}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      disabled={kycStatus === 'pending'}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Proof of Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Proof of Address *
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-white">
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileChange('address_proof', e.target.files?.[0] || null)}
                  className="hidden"
                  id="address-proof"
                  disabled={kycStatus === 'pending'}
                />
                {!previews.address_proof ? (
                  <label
                    htmlFor="address-proof"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600 mb-1">Click to upload proof of address</span>
                    <span className="text-xs text-gray-500">PNG, JPG, PDF up to 5MB</span>
                  </label>
                ) : (
                  <div className="relative bg-white">
                    <img
                      src={previews.address_proof}
                      alt="Proof of Address"
                      className="w-full h-48 object-contain border border-gray-300 rounded-lg bg-white"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveFile('address_proof')}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      disabled={kycStatus === 'pending'}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            {kycStatus !== 'pending' && (
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || !kycDocuments.document_front || !kycDocuments.address_proof}
                  className="flex-1 bg-gray-800 hover:bg-gray-900"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit KYC Documents'}
                </Button>
              </div>
            )}
          </form>
        </Card>
      </div>
      </div>
    </>
  );
};

export default MarketplaceKYC;
